import { ProcessedCourse } from "@/common/course/types";
import getProgramPacing from "@/lib/program/utils/getProgramPacing";
import getProgramDuration from "@/lib/program/utils/getProgramDuration";
import getTotalEstimatedEffortInHoursPerWeek from "@/lib/program/utils/getTotalEstimatedEffortInHoursPerWeek";

export type CourseInformation = {
  header: string;
  description: string;
  icon: string;
  overviewLink?: string;
};

export type ProgramInfoTranslate = (
  key: string,
  values?: Record<string, string | number | Date>,
) => string;

function getLevelMapped(t: ProgramInfoTranslate) {
  return {
    Introductory: {
      title: t("levelIntroductory"),
      description: t("noPriorExperienceRequired"),
    },
    Intermediate: {
      title: t("levelIntermediate"),
      description: t("somePriorExperienceRequired"),
    },
    Advanced: {
      title: t("levelAdvanced"),
      description: t("extensivePriorExperienceRequired"),
    },
  };
}

export function getProgramInformation(
  courses: ProcessedCourse[],
  isHeaderFactoidBar = false,
  t: ProgramInfoTranslate,
): CourseInformation[] {
  const pacingType = getProgramPacing(courses);
  const duration = courses && getProgramDuration(courses);

  const courseInformation = [];
  const effortInformation = duration
    ? {
        header: `${duration}`,
        description: `${getTotalEstimatedEffortInHoursPerWeek(courses)}`,
        icon: "timelapse",
      }
    : null;

  const pacingInformation = {
    header: pacingType === "self_paced" ? t("selfPaced") : t("instructorPaced"),
    description:
      pacingType === "self_paced"
        ? t("progressAtYourOwnSpeed")
        : t("instructorLedOnSchedule"),
    icon: "person",
  };

  const levelMapped = getLevelMapped(t);
  const level = levelMapped[courses[0].levelType as keyof typeof levelMapped];

  const levelInformation =
    pacingType === "instructor_paced" || !level
      ? null
      : {
          header: level.title,
          description: level.description,
          icon: "speedometer",
        };

  const coursesInformation = {
    header: isHeaderFactoidBar
      ? t("earnCertificatesCount", { count: courses.length })
      : t("coursesCount", { count: courses.length }),
    description: isHeaderFactoidBar
      ? t("advanceYourCareerToday")
      : t("learnMore"),
    icon: "certificate",
    overviewLink: "#courses",
  };

  if (levelInformation) courseInformation.push(levelInformation);
  courseInformation.push(pacingInformation);
  if (effortInformation) courseInformation.push(effortInformation);
  courseInformation.push(coursesInformation);

  return courseInformation;
}
